import { Component } from '@theme/component';
import { prefersReducedMotion, isTouchDevice } from '@theme/utilities';

/**
 * A grid of square panels that cross-fade from a static image to a looping
 * muted video.
 *
 * Desktop/keyboard: hover or focus starts the preview; leaving/blurring stops
 * it. Clicking always navigates natively — the preview already happened
 * before the click.
 *
 * Touch: there is no hover phase, so a first tap on a panel with a video
 * starts the preview instead of navigating (the click is intercepted). A
 * second tap on that same, already-armed panel is allowed through and
 * navigates normally. Tapping a different panel disarms the first.
 *
 * Panels without a video, and all panels when `prefers-reduced-motion` is
 * set, are never intercepted — they behave as plain links.
 *
 * @typedef {Object} Refs
 * @property {HTMLAnchorElement[]} panels
 *
 * @extends {Component<Refs>}
 */
export class CategoryVideoGrid extends Component {
  requiredRefs = ['panels'];

  /** @type {number | null} */
  #armedIndex = null;

  get panels() {
    return this.refs.panels ?? [];
  }

  /** @param {PointerEvent} event */
  handlePointerEnter(event) {
    if (isTouchDevice()) return;
    this.#startPreview(/** @type {HTMLElement} */ (event.target));
  }

  /** @param {PointerEvent} event */
  handlePointerLeave(event) {
    if (isTouchDevice()) return;
    this.#stopPreview(/** @type {HTMLElement} */ (event.target));
  }

  /** @param {FocusEvent} event */
  handleFocus(event) {
    this.#startPreview(/** @type {HTMLElement} */ (event.target));
  }

  /** @param {FocusEvent} event */
  handleBlur(event) {
    this.#stopPreview(/** @type {HTMLElement} */ (event.target));
  }

  /** @param {MouseEvent} event */
  handleClick(event) {
    const panel = /** @type {HTMLElement} */ (event.target);
    const video = panel.querySelector('video');

    if (!video || prefersReducedMotion() || !isTouchDevice()) return;

    const index = this.panels.indexOf(/** @type {HTMLAnchorElement} */ (panel));

    if (this.#armedIndex === index) {
      // Second tap on the already-armed panel: let it navigate.
      this.#armedIndex = null;
      return;
    }

    // First tap: preview instead of navigating, and disarm any other panel.
    event.preventDefault();
    this.#disarmAll();
    this.#armedIndex = index;
    this.#startPreview(panel);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#disarmAll();
  }

  /** @param {HTMLElement} panel */
  #startPreview(panel) {
    const video = panel.querySelector('video');
    if (!video || prefersReducedMotion()) return;

    panel.dataset.previewing = 'true';
    video.muted = true;
    video.currentTime = 0;
    video.play().catch(() => {});

    if (isTouchDevice()) {
      const status = this.#statusFor(panel);
      const title = panel.querySelector('.category-video-grid__title')?.textContent?.trim();
      if (status) status.textContent = `Preview playing. Select ${title ?? 'this panel'} again to continue.`;
    }
  }

  /** @param {HTMLElement} panel */
  #stopPreview(panel) {
    const video = panel.querySelector('video');

    delete panel.dataset.previewing;

    if (video) {
      video.pause();
      video.currentTime = 0;
    }

    const status = this.#statusFor(panel);
    if (status) status.textContent = '';

    const index = this.panels.indexOf(/** @type {HTMLAnchorElement} */ (panel));
    if (this.#armedIndex === index) this.#armedIndex = null;
  }

  #disarmAll() {
    for (const panel of this.panels) this.#stopPreview(panel);
  }

  /** @param {HTMLElement} panel */
  #statusFor(panel) {
    return panel.querySelector('.category-video-grid__status');
  }
}

if (!customElements.get('category-video-grid-component')) {
  customElements.define('category-video-grid-component', CategoryVideoGrid);
}
